import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { Mail, Lock, Eye, EyeOff, CheckCircle, AlertTriangle, ArrowLeft, Loader2, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';


import { translateAuthError } from '../../utils/authErrors';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Login() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (searchParams.get('reset') === 'success') {
      setSuccessMsg(t('auth.login.reset_success'));
    }
  }, [searchParams, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        window.REACT_APP_NAVIGATE('/dashboard');
      }
    } catch (err: any) {
      setError(translateAuthError(err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg transition-colors duration-300 flex items-center justify-center px-4 py-12 relative">
      <SEO
        title={t('auth.login.title')}
        description={t('auth.login.seo_description')}
        canonical="/login"
      />
      <div className="absolute top-4 right-4">

        <button
          onClick={toggleTheme}
          className="p-2.5 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
          title={theme === 'dark' ? t('dashboard.sidebar.mode_light') || 'Mode clair' : t('dashboard.sidebar.mode_dark') || 'Mode sombre'}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </div>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 shrink-0">
              <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-3xl font-bold text-text-main">CitoyenPro</span>
          </Link>
          <h1 className="text-3xl font-bold text-text-main mb-2">{t('auth.login.welcome')}</h1>
          <p className="text-text-muted">{t('auth.login.subtitle')}</p>
        </div>

        {/* Login Form */}
        <div className="bg-dark-card rounded-2xl border border-dark-border p-8 shadow-xl">
          {successMsg && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
              <p className="text-sm text-green-500 flex items-center gap-2">
                <CheckCircle size={16} />
                {successMsg}
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-sm text-red-500 flex items-center gap-2">
                <AlertTriangle size={16} />
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-text-main mb-2">
                {t('auth.login.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="text-text-muted" size={20} />
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                  placeholder={t('auth.login.email_placeholder')}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-text-main mb-2">
                {t('auth.login.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="text-text-muted" size={20} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                  placeholder={t('auth.login.password_placeholder')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer"
                >
                  {showPassword ? (
                    <EyeOff className="text-text-muted hover:text-text-main" size={20} />
                  ) : (
                    <Eye className="text-text-muted hover:text-text-main" size={20} />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" className="w-4 h-4 text-primary bg-dark-bg border-dark-border rounded focus:ring-primary cursor-pointer accent-primary" />
                <span className="ml-2 text-sm text-text-muted">{t('auth.login.remember_me')}</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-primary hover:text-primary-hover font-medium cursor-pointer">
                {t('auth.login.forgot_password')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {t('auth.login.logging_in')}
                </>
              ) : (
                t('auth.login.submit')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-text-muted">
              {t('auth.login.no_account')}{' '}
              <Link to="/signup" className="text-text-main hover:text-primary font-semibold transition-colors">
                {t('auth.login.create_account')}
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-text-muted hover:text-text-main text-sm flex items-center justify-center gap-2 transition-colors">
            <ArrowLeft size={16} />
            {t('auth.login.back_home')}
          </Link>
        </div>
      </div>
    </div >
  );
}
